import { useMemo, useState, useEffect } from "react";
import { FiPlus } from "react-icons/fi";

import AdminLayout from "@/layouts/AdminLayout";
import Breadcrumbs from "@/components/shared/nav/Breadcrumbs";
import Modal from "@/components/shared/ui/Modal";
import ConfirmDialog from "@/components/shared/ui/ConfirmDialog";
import Input from "@/components/shared/ui/Input";

import AirlinesTable, { type AirlineTableFilters, type AirlineRow } from "@/components/airlines/AirlinesTable";
import CreateAirline, { type UpsertAirline } from "@/components/airlines/forms/CreateAirline";
import UpdateAirline from "@/components/airlines/forms/UpdateAirline";

import {
  useAirlinesList,
  useCreateAirlineMutation,
  useUpdateAirlineMutation,
  useDeleteAirlineMutation,
} from "@/api/features/airlines";
import type { Airline as DtoAirline } from "@/api/features/airlines";
import { assetUrl } from "@/lib/assets";

function resolveLogoSrc(path?: string | null) {
  const p = String(path ?? "").trim();
  if (!p) return null;
  if (/^https?:\/\//i.test(p)) return p;
  const normalized = p.startsWith("assets/") ? p : `assets/${p.replace(/^\/+/, "")}`;
  return assetUrl(normalized);
}

export default function ViewAirlines() {
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState<null | DtoAirline>(null);
  const [viewOpen, setViewOpen] = useState<null | DtoAirline>(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState("Please confirm");
  const [confirmMsg, setConfirmMsg] = useState("");
  const [confirmCta, setConfirmCta] = useState("Confirm");
  const [confirmAction, setConfirmAction] = useState<() => void>(() => () => {});

  const [createError, setCreateError] = useState<string | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [filters, setFilters] = useState<AirlineTableFilters>({});
  const [globalQ, setGlobalQ] = useState("");

  const [page, setPage] = useState(1);
  const DEFAULT_PAGE_SIZE = 20;

  useEffect(() => setPage(1), [filters, globalQ]);

  const q = useMemo(() => {
    const bits = [globalQ, filters.code, filters.name].filter(Boolean);
    return bits.length ? bits.join(" ").trim() : undefined;
  }, [globalQ, filters.code, filters.name]);

  const { data, isLoading, isError, error, refetch } = useAirlinesList({
    page,
    limit: DEFAULT_PAGE_SIZE,
    q,
  });

  const total = data?.total ?? 0;
  const pageSize = data?.pageSize ?? DEFAULT_PAGE_SIZE;
  const currentPage = data?.page ?? page;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const pageRows: AirlineRow[] = (data?.items ?? []).map((a) => ({
    id: a.id,
    code: a.code,
    name: a.name,
    logoPath: a.logoPath ?? null,
  }));

  const createMut = useCreateAirlineMutation();
  const updateMut = useUpdateAirlineMutation(editOpen?.id ?? 0);
  const deleteMut = useDeleteAirlineMutation();

  const onCreate = async (payload: UpsertAirline) => {
    setCreateError(null);
    try {
      await createMut.mutateAsync({
        code: payload.code.trim().toUpperCase(),
        name: payload.name.trim(),
        logoPath: payload.logoPath?.trim() ? payload.logoPath.trim() : null,
      });
      setCreateOpen(false);
      refetch();
    } catch (e: any) {
      setCreateError(e?.message || "Failed to create airline");
    }
  };

  const onEdit = async (payload: UpsertAirline) => {
    if (!editOpen) return;
    setUpdateError(null);
    try {
      await updateMut.mutateAsync({
        code: payload.code?.trim() ? payload.code.trim().toUpperCase() : undefined,
        name: payload.name?.trim() ? payload.name.trim() : undefined,
        logoPath: payload.logoPath?.trim() ? payload.logoPath.trim() : null,
      });
      setEditOpen(null);
      refetch();
    } catch (e: any) {
      setUpdateError(e?.message || "Failed to update airline");
    }
  };

  const askDelete = (a: AirlineRow) => {
    setDeleteError(null);
    setConfirmTitle("Delete airline");
    setConfirmMsg(`Do you want to delete the airline “${a.code} — ${a.name}”? This action cannot be undone.`);
    setConfirmCta("Delete");
    setConfirmAction(() => async () => {
      try {
        await deleteMut.mutateAsync(Number(a.id));
        setConfirmOpen(false);
        refetch();
      } catch (e: any) {
        setDeleteError(e?.message || "Failed to delete airline");
      }
    });
    setConfirmOpen(true);
  };

  const serialStart = (currentPage - 1) * pageSize;

  return (
    <AdminLayout>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Airlines</h1>
          <div className="mt-1"><Breadcrumbs /></div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <svg className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
            <Input value={globalQ} onChange={(e) => setGlobalQ(e.target.value)} placeholder="Search airlines..." className="w-56 pl-8" />
          </div>
          <button className="inline-flex items-center justify-center gap-2 rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-700" onClick={() => setCreateOpen(true)}><FiPlus /> Add Airline</button>
        </div>
      </div>

      {isError && <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">Failed to load airlines: {(error as Error)?.message || "Unknown error"}</div>}
      {createError && <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{createError}</div>}
      {updateError && <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{updateError}</div>}
      {deleteError && <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{deleteError}</div>}

      <AirlinesTable
        pageRows={pageRows}
        serialStart={serialStart}
        filters={filters}
        setFilters={setFilters}
        onView={(r) => {
          // Fall back to the row itself: it already carries everything the
          // details modal shows, so a lookup miss can never leave the button
          // doing nothing at all.
          const full = (data?.items ?? []).find((x) => x.id === r.id);
          setViewOpen(
            full ?? {
              id: Number(r.id),
              code: r.code,
              name: r.name,
              logoPath: r.logoPath ?? null,
            }
          );
        }}
        onEdit={(r) => {
          const full = (data?.items ?? []).find((x) => x.id === r.id) as DtoAirline | undefined;
          if (full) setEditOpen(full);
        }}
        onDelete={askDelete}
      />

      <div className="mt-0 flex items-center justify-between gap-2 rounded-b-2xl border border-t-0 border-gray-200 bg-white px-4 py-3">
        <div className="text-xs text-gray-500">
          {isLoading ? <span>Loading...</span> : <>Showing <span className="font-medium text-gray-700">{total === 0 ? 0 : serialStart + 1}</span> to <span className="font-medium text-gray-700">{Math.min(serialStart + pageSize, total)}</span> of <span className="font-medium text-gray-700">{total}</span> results</>}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1 || isLoading} className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50">Prev</button>
          <div className="hidden items-center gap-1 sm:flex">
            {Array.from({ length: Math.min(totalPages, 6) }).map((_, idx) => {
              const n = idx + 1;
              const active = n === currentPage;
              return (
                <button key={n} onClick={() => setPage(n)} className={`rounded-md px-2.5 py-1.5 text-sm ${active ? "bg-sky-600 text-white" : "border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"}`}>
                  {n}
                </button>
              );
            })}
            {totalPages > 6 && <span className="px-1 text-sm text-gray-500">...</span>}
          </div>
          <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages || isLoading} className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50">Next</button>
        </div>
      </div>

      <Modal title="Create Airline" open={createOpen} onClose={() => setCreateOpen(false)}>
        <CreateAirline onSubmit={onCreate} onCancel={() => setCreateOpen(false)} />
      </Modal>

      <Modal title="Edit Airline" open={!!editOpen} onClose={() => setEditOpen(null)}>
        {editOpen ? (
          <UpdateAirline
            airline={{ id: editOpen.id, code: editOpen.code, name: editOpen.name, logoPath: editOpen.logoPath ?? "" }}
            onSubmit={onEdit}
            onCancel={() => setEditOpen(null)}
          />
        ) : null}
      </Modal>

      <Modal title="Airline Details" open={!!viewOpen} onClose={() => setViewOpen(null)}>
        {viewOpen ? (
          <div className="space-y-3 text-center">
            <div className="mx-auto h-20 w-20 overflow-hidden rounded-xl bg-gray-100 ring-1 ring-gray-200">
              {resolveLogoSrc(viewOpen.logoPath) ? (
                <img
                  src={resolveLogoSrc(viewOpen.logoPath)!}
                  alt={`${viewOpen.name} logo`}
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.display = "none";
                  }}
                />
              ) : null}
            </div>
            <div className="text-base font-semibold text-gray-900">{viewOpen.name}</div>
            <div className="text-sm text-gray-600">Code: {viewOpen.code}</div>
          </div>
        ) : null}
      </Modal>

      <ConfirmDialog open={confirmOpen} title={confirmTitle} message={confirmMsg} confirmText={confirmCta} cancelText="Cancel" onCancel={() => setConfirmOpen(false)} onConfirm={confirmAction} />
    </AdminLayout>
  );
}
